import BasicButton from "components/General/BasicButton";
import { useNavigate } from "react-router-dom";

import { landTypes } from "utils/game-information-constants";
import InfoPopout from "components/Wrappers/InfoPopout";
import { LandToolTips } from "utils/ToolTipConst";

import { HIGHLIGHTS } from "utils/ToolTipConst";
import PopText from "components/Wrappers/PopText";

const standardClassname = "space-y-2 text-sm p4";
const actionTutorialContent: { [key: string]: React.ReactNode } = {};

export default function BasicTutorial() {
  return (
    <div>
      <div className={standardClassname}>
        <p className="text-2xl font-bold text-center mb-4">
          Game Overview Tutorial
        </p>
        <p>
          Welcome to the wetlands management game. Here, each player takes on a{" "}
          <PopText recordName="role" text="role" recordType="generic" /> with
          unique objectives and incentives. You can hover over and underlined
          text for an in-depth explanation. More detailed information about your
          own role will be availible on the next tutorial page.
        </p>
        <p>
          The game world is represented by a{" "}
          <PopText recordName="map" text="map" recordType="generic" />, divided
          into{" "}
          <PopText
            recordName="management unit"
            text="Management Units"
            recordType="generic"
          />{" "}
          and individual{" "}
          <PopText recordName="cell" text="cells" recordType="generic" />.
        </p>

        <p className="text-lg font-bold text-center mb-2">Roles</p>
        <p>
          There are six different{" "}
          <PopText recordName="role" text="roles" recordType="generic" /> split
          across two{" "}
          <PopText recordName="estate" text="estates" recordType="generic" />:
          the public and the private. Roles include{" "}
          <PopText
            recordName="Public Hunter"
            text="Public Hunter"
            recordType="role"
          />
          ,{" "}
          <PopText
            recordName="Private Hunter"
            text="Private Hunter"
            recordType="role"
          />
          ,{" "}
          <PopText
            recordName="Reed Harvester"
            text="Reed Harvester"
            recordType="role"
          />
          ,{" "}
          <PopText
            recordName="Stockbreeder"
            text="Stockbreeder"
            recordType="role"
          />
          , and{" "}
          <PopText
            recordName="Conservationist"
            text="Conservationist"
            recordType="role"
          />
          .
        </p>
        <p>
          Each role has different ways of earning{" "}
          <PopText recordName="money" text="money" recordType="generic" /> and
          different goals. Some roles, such as the{" "}
          <PopText
            recordName="Conservationist"
            text="Conservationist"
            recordType="role"
          />
          , focus on ecology, while others like the{" "}
          <PopText
            recordName="Reed Harvester"
            text="Reed Harvester"
            recordType="role"
          />{" "}
          or{" "}
          <PopText
            recordName="Stockbreeder"
            text="Stockbreeder"
            recordType="role"
          />{" "}
          seek economic profit.
        </p>

        <p className="text-lg font-bold text-center mb-2">Map and Estates</p>
        <p>
          The <PopText recordName="map" text="map" recordType="generic" /> is
          the central play area, split into two halves: the public and the
          private{" "}
          <PopText recordName="estate" text="estates" recordType="generic" />.
          Unless you are the{" "}
          <PopText
            recordName="Conservationist"
            text="Conservationist"
            recordType="role"
          />
          , you can only see your own estate’s half. Each estate has its own
          opportunities and challenges: the private estate is{" "}
          <span className={HIGHLIGHTS.positive}>
            more profitable for hunting
          </span>
          , while the public estate has{" "}
          <span className={HIGHLIGHTS.negative}>
            greater competition for control
          </span>
          .
        </p>
        <p>
          The map consists of{" "}
          <PopText
            recordName="management unit"
            text="management units"
            recordType="generic"
          />{" "}
          containing individual{" "}
          <PopText recordName="cell" text="cells" recordType="generic" />.
          Seasonal{" "}
          <PopText
            recordName="water regime"
            text="Water Management Regimes"
            recordType="generic"
          />
          chosen at the{" "}
          <PopText recordName="townhall" text="Townhall" recordType="generic" />
          determine flooding and drying, which directly impacts resources and
          actions.
        </p>

        <p className="text-lg font-bold text-center mb-2">
          Basic UI and Gameplay Loop
        </p>
        <p>
          Use the action bar to select tools such as{" "}
          <PopText recordName="clearing" text="Clearing" recordType="action" />,{" "}
          <PopText recordName="cut" text="Cutting" recordType="action" />,{" "}
          <PopText recordName="grazing" text="Grazing" recordType="action" />,{" "}
          <PopText
            recordName="private-hunt"
            text="Hunting"
            recordType="action"
          />
          , and{" "}
          <PopText
            recordName="create-nature-reserve"
            text="Nature Reserve"
            recordType="action"
          />
          . These actions let you modify the landscape, harvest resources, and
          pursue your role’s goals. You can simply click on the action to select it, then click on the map to perform that action. Tutorials specific to each action are availible.
        </p>
        <p>
          On the right of the screen is a chat menu.{" "}
          <span className={HIGHLIGHTS.neutral}>
            From here you can send and communications with other players.
          </span>
        </p>
        <p>
          At the end of each year, players meet at the{" "}
          <PopText recordName="townhall" text="Townhall" recordType="generic" />{" "}
          to vote on the next season’s{" "}
          <PopText
            recordName="water regime"
            text="water management regime"
            recordType="generic"
          />
          . The chosen regime will affect{" "}
          <span className={HIGHLIGHTS.positive}>bird populations</span>,{" "}
          <span className={HIGHLIGHTS.negative}>grazing potential</span>, and{" "}
          <span className={HIGHLIGHTS.neutral}>reed harvesting conditions</span>
          .
        </p>

        <p>
          Success requires balancing your personal objectives with negotiation
          and cooperation with other players. Use your{" "}
          <span className={HIGHLIGHTS.positive}>role’s strengths</span> wisely
          and adapt to the changing environment of the wetlands.
        </p>
      </div>
    </div>
  );
}
